import arriereBackoffice, {
  OnlineTravelAgency,
  UUID,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { unitsSearchFragment } from '@arriere/modules/propertiesList'
import { layoutImagesFragment } from '@arriere/modules/propertyLayout'
import {
  HotelPublicSpaceModificationData,
  HotelRoomTypeModificationData,
  UpdateHotelPublicSpace,
  UpdateHotelRoom,
} from '@arriere/types/HotelLayout.types'

import { customFieldsFragment } from './customFields'

export const hotelAmenitiesFragment = {
  amenityId: true,
  quantity: true,
  amenity: {
    name: true,
  },
}

const sharedLayoutFragment = {
  gallery: {
    ...layoutImagesFragment,
  },
  spaces: {
    kind: true,
    quantity: true,
  },
  sleepingPlacesAmenities: {
    ...hotelAmenitiesFragment,
  },
  roomAmenities: {
    ...hotelAmenitiesFragment,
  },
}

export const publicSpaceFragment = {
  id: true,
  name: true,
  description: true,
  listOrder: true,
  active: true,
  ...sharedLayoutFragment,
}

export const hotelRoomTypeFragment = {
  hotelRoomTypeId: true,
  name: true,
  title: true,
  maxOccupancy: true,
  bedsCount: true,
  bathroomsCount: true,
  bedroomsCount: true,
  description: true,
  units: {
    ...unitsSearchFragment,
  },
  referenceUnit: {
    id: true,
    name: true,
    unitNumber: true,
  },
  listings: {
    __args: { agencies: [OnlineTravelAgency.airbnb, OnlineTravelAgency.vrbo] },
    agency: true,
    title: true,
  },
  listingLinks: {
    kind: true,
    leadSource: true,
    url: true,
  },
  customFields: { fields: customFieldsFragment.fields },
  ...sharedLayoutFragment,
}

// Queries

export const getHotelRoomTypesList = async (hotelId: string) => {
  return arriereBackoffice.queries.hotelRoomType
    .raw({
      __alias: 'getHotelRoomTypesList',
      list: {
        __args: { hotelId },
        ...hotelRoomTypeFragment,
      },
    })
    .then(async ({ data, errors }) => {
      if (errors && errors?.length) {
        throw new Error(errors[0].message)
      }

      return data.list
    })
}

export const getHotelPublicSpacesList = async (hotelId: string) => {
  return arriereBackoffice.queries.hotel
    .raw({
      __alias: 'getHotelPublicSpacesList',
      get: {
        __args: { hotelId },
        hotel: {
          publicSpaces: {
            ...publicSpaceFragment,
          },
        },
      },
    })
    .then(async ({ data, errors }) => {
      if (errors && errors?.length) {
        throw new Error(errors[0].message)
      }

      return data.get?.hotel.publicSpaces
    })
}

const amenitiesFragment = {
  id: true,
  name: true,
}

export const getHotelRoomsAmenities = async () => {
  return arriereBackoffice.queries.hotelRoomType
    .raw({
      __alias: 'getHotelRoomsAmenities',
      hotelRoomTypeAmenities: {
        sleepingPlaces: {
          ...amenitiesFragment,
        },
        roomAmenities: {
          ...amenitiesFragment,
        },
        bathroomAmenities: {
          ...amenitiesFragment,
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors && errors?.length) {
        throw new Error(errors[0].message)
      }

      return data.hotelRoomTypeAmenities
    })
}

export const getPhotoGalleryById = async (contentReferenceUnitId: string) => {
  return arriereBackoffice.queries.home
    .raw({
      __alias: 'getPhotoGalleryById',
      __args: { homeId: contentReferenceUnitId },
      images: {
        listOrder: true,
        description: true,
        roomId: true,
        agencyOverrides: {
          agency: true,
          listOrder: true,
        },
        asset: {
          ...layoutImagesFragment,
        },
      },
    })
    .then(async ({ data, errors }) => {
      if (errors && errors?.length) {
        throw new Error(errors[0].message)
      }

      return data.images
    })
}

// Mutations

export const createHotelRoom = async (data: HotelRoomTypeModificationData) => {
  return arriereBackoffice.mutations.hotelRoomType({
    __alias: 'createHotelRoom',
    create: {
      __args: { data },
      id: true,
    },
  })
}

export const archiveHotelRoom = async (hotelRoomTypeId: string) => {
  return arriereBackoffice.mutations.hotelRoomType({
    __alias: 'archiveHotelRoom',
    archive: {
      __args: { hotelRoomTypeId },
    },
  })
}

export const updateHotelRoom = async ({ hotelRoomTypeId, data }: UpdateHotelRoom) => {
  return arriereBackoffice.mutations.hotelRoomType({
    __alias: 'updateHotelRoom',
    update: {
      __args: { hotelRoomTypeId, data },
      id: true,
    },
  })
}

export interface UpdateHotelRoomTypeCustomFieldsProps {
  hotelRoomTypeId: string
  customFields: Array<{ customFieldId: UUID; value: string }>
}

export const updateHotelRoomTypeCustomFields = async ({
  customFields,
  hotelRoomTypeId,
}: UpdateHotelRoomTypeCustomFieldsProps) => {
  return arriereBackoffice.mutations.hotelRoomType({
    __alias: 'updateHotelRoomTypeCustomFields',
    updateCustomFields: {
      __args: { hotelRoomTypeId, customFields },
    },
  })
}

export const updateHotelRoomOrderList = async (hotelRoomTypeIds: string[]) => {
  return arriereBackoffice.mutations.hotelRoomType({
    __alias: 'updateHotelRoomOrderList',
    updateOrderList: {
      __args: { hotelRoomTypeIds },
    },
  })
}

export const createHotelPublicSpace = async (space: HotelPublicSpaceModificationData) => {
  return arriereBackoffice.mutations.hotel.raw({
    __alias: 'createHotelPublicSpace',
    createHotelPublicSpace: {
      __args: { space },
      id: true,
    },
  })
}

export const updateHotelPublicSpace = async ({ publicSpaceId, space }: UpdateHotelPublicSpace) => {
  return arriereBackoffice.mutations.hotel({
    __alias: 'updateHotelPublicSpace',
    updateHotelPublicSpace: {
      __args: { publicSpaceId, space },
      id: true,
    },
  })
}

export const deleteHotelPublicSpace = async (id: string) => {
  return arriereBackoffice.mutations.hotel({
    __alias: 'deleteHotelPublicSpace',
    deleteHotelPublicSpace: {
      __args: { id },
    },
  })
}

export const updateHotelPublicSpaceOrder = async (publicSpaceIds: string[]) => {
  return arriereBackoffice.mutations.hotel({
    __alias: 'updateHotelPublicSpaceOrder',
    updateHotelPublicSpaceOrder: {
      __args: { ids: publicSpaceIds },
    },
  })
}
